import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Lock, Users, Truck, Banknote, Tags, ListTree } from "lucide-react";
import { formatRupees } from "@/lib/format";
import InvestmentTagsField from "@/components/InvestmentTagsField";

const TABS = [
  { key: "customers", label: "Customers", icon: Users },
  { key: "vendors", label: "Vendors", icon: Truck },
  { key: "accounts", label: "Accounts", icon: Banknote },
  { key: "categories", label: "Categories", icon: Tags },
  { key: "subcategories", label: "Sub-categories", icon: ListTree },
];

export default function Masters() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "accounts";
  const canAdmin = user?.role === "admin";
  const [tab, setTab] = useState("customers");

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow">Configuration</div>
        <h1 className="mt-2 font-editorial font-light text-[38px] leading-[1.02] sm:text-5xl md:text-6xl text-slate-900 tracking-[-0.035em]" data-testid="masters-title">
          <em className="italic">Masters.</em>
        </h1>
        <p className="mt-2 text-sm text-slate-500 max-w-md">
          The reference lists your books draw from — parties you deal with, accounts you operate, and the heads you group money by.
        </p>
      </div>

      <div className="flex items-center gap-1 flex-wrap border-b border-slate-200 pb-0">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`master-tab-${t.key}`}
            className={`flex items-center gap-2 px-3 py-2 text-[13px] font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key ? "border-red-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            <t.icon className="h-4 w-4" strokeWidth={1.75} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "customers" && <PartyTable kind="customer" canEdit={canEdit} canAdmin={canAdmin} />}
      {tab === "vendors" && <PartyTable kind="vendor" canEdit={canEdit} canAdmin={canAdmin} />}
      {tab === "accounts" && <AccountTable canEdit={canEdit} canAdmin={canAdmin} />}
      {tab === "categories" && <CategoryTable canEdit={canEdit} canAdmin={canAdmin} />}
      {tab === "subcategories" && <SubCategoryTable canEdit={canEdit} canAdmin={canAdmin} />}
    </div>
  );
}

/* ----- Customers / Vendors ----- */
function PartyTable({ kind, canEdit, canAdmin }) {
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", gstin: "", pan: "", address: "", contact_person: "", notes: "", investment_ids: [] });

  const load = () => api.get(`/parties?kind=${kind}`).then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const open = (e = null) => {
    setEditing(e);
    setForm(e ? { ...e, investment_ids: e.investment_ids || [] } : { name: "", phone: "", email: "", gstin: "", pan: "", address: "", contact_person: "", notes: "", investment_ids: [] });
    setDlg(true);
  };

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      if (editing) await api.put(`/parties/${editing.id}`, { ...form, kind });
      else await api.post("/parties", { ...form, kind });
      toast.success(editing ? "Updated" : "Added");
      setDlg(false); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/parties/${id}`);
    toast.success("Deleted"); await load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-500">{items.length} {kind === "customer" ? "customers" : "vendors"}</div>
        {canEdit && (
          <Button onClick={() => open()} className="rounded-md bg-red-600 hover:bg-red-700 text-white gap-2" data-testid={`add-${kind}`}>
            <Plus className="h-4 w-4" /> Add {kind}
          </Button>
        )}
      </div>
      <div className="border border-slate-200 rounded-md bg-white overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {["Name", "Contact", "Phone / Email", "GSTIN", "PAN", ""].map((h) => (
                <th key={h} className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/70">
                <td className="p-3 font-medium text-slate-900">{p.name}</td>
                <td className="p-3 text-slate-600">{p.contact_person || "—"}</td>
                <td className="p-3 text-slate-600 text-[12px]">
                  <div>{p.phone || "—"}</div>
                  <div className="text-slate-400">{p.email || ""}</div>
                </td>
                <td className="p-3 text-slate-600 font-mono-fin text-[12px]">{p.gstin || "—"}</td>
                <td className="p-3 text-slate-600 font-mono-fin text-[12px]">{p.pan || "—"}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  {canEdit && (
                    <div className="inline-flex gap-1">
                      <button onClick={() => open(p)} className="p-1 text-slate-400 hover:text-slate-700"><Pencil className="h-3.5 w-3.5" /></button>
                      {canAdmin && <button onClick={() => remove(p.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center text-slate-500 text-sm">No {kind}s yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} {kind}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" data-testid="party-name" /></div>
              <div><Label>Contact person</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="mt-2" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2" /></div>
              <div><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} className="mt-2" /></div>
              <div><Label>PAN</Label><Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} className="mt-2" /></div>
            </div>
            <div><Label>Address</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} className="mt-2" /></div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-2" /></div>
            <InvestmentTagsField
              value={form.investment_ids || []}
              onChange={(ids) => setForm({ ...form, investment_ids: ids })}
              testId={`${kind}-inv-tags`}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)} className="rounded-md">Cancel</Button>
            <Button onClick={save} className="rounded-md bg-red-600 hover:bg-red-700 text-white" data-testid="party-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ----- Accounts ----- */
function AccountTable({ canEdit, canAdmin }) {
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", type: "bank", bank_name: "", account_number: "", opening_balance: "", opening_date: "", note: "", is_active: true, investment_ids: [] });

  const load = () => api.get("/accounts").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const open = (e = null) => {
    setEditing(e);
    setForm(e ? { ...e, opening_balance: e.opening_balance || 0, investment_ids: e.investment_ids || [] } :
      { name: "", type: "bank", bank_name: "", account_number: "", opening_balance: "", opening_date: "", note: "", is_active: true, investment_ids: [] });
    setDlg(true);
  };
  const save = async () => {
    if (!form.name) return toast.error("Name required");
    const payload = { ...form, opening_balance: Number(form.opening_balance || 0) };
    try {
      if (editing) await api.put(`/accounts/${editing.id}`, payload);
      else await api.post("/accounts", payload);
      toast.success(editing ? "Updated" : "Added");
      setDlg(false); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this account?")) return;
    try { await api.delete(`/accounts/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-500">{items.length} accounts</div>
        {canEdit && (
          <Button onClick={() => open()} className="rounded-md bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="add-account">
            <Plus className="h-4 w-4" /> Add account
          </Button>
        )}
      </div>
      <div className="border border-slate-200 rounded-md bg-white overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>{["Name", "Type", "Bank", "Account no.", "Opening", "Balance", ""].map((h) => (
              <th key={h} className={`p-3 text-[10px] tracking-wider uppercase text-slate-500 ${["Opening", "Balance"].includes(h) ? "text-right" : "text-left"}`}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50/70">
                <td className="p-3 font-medium">{a.name}</td>
                <td className="p-3 text-slate-600 capitalize">{a.type}</td>
                <td className="p-3 text-slate-600">{a.bank_name || "—"}</td>
                <td className="p-3 text-slate-600 font-mono-fin text-[12px]">{a.account_number || "—"}</td>
                <td className="p-3 text-right font-mono-fin text-slate-600">{formatRupees(a.opening_balance || 0)}</td>
                <td className="p-3 text-right font-mono-fin font-medium text-slate-900">{formatRupees(a.balance || 0)}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  {canEdit && (
                    <div className="inline-flex gap-1">
                      <button onClick={() => open(a)} className="p-1 text-slate-400 hover:text-slate-700"><Pencil className="h-3.5 w-3.5" /></button>
                      {canAdmin && <button onClick={() => remove(a.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit account" : "Add account"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" data-testid="acc-name" /></div>
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Bank name</Label><Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="mt-2" /></div>
              <div><Label>Account number</Label><Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} placeholder="last 4 digits" className="mt-2" /></div>
              <div><Label>Opening balance</Label><Input type="number" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} className="mt-2" data-testid="acc-open" /></div>
              <div><Label>Opening date</Label><Input type="date" value={form.opening_date || ""} onChange={(e) => setForm({ ...form, opening_date: e.target.value })} className="mt-2" /></div>
            </div>
            <div><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} className="mt-2" /></div>
            <InvestmentTagsField
              value={form.investment_ids || []}
              onChange={(ids) => setForm({ ...form, investment_ids: ids })}
              testId="acc-inv-tags"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)} className="rounded-md">Cancel</Button>
            <Button onClick={save} className="rounded-md bg-red-600 hover:bg-red-700 text-white" data-testid="acc-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ----- Categories ----- */
function CategoryTable({ canEdit, canAdmin }) {
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", kind: "expense", color: "#64748B", investment_ids: [] });

  const load = () => api.get("/categories").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const open = (e = null) => {
    setEditing(e);
    setForm(e ? { name: e.name, kind: e.kind, color: e.color || "#64748B", investment_ids: e.investment_ids || [] } : { name: "", kind: "expense", color: "#64748B", investment_ids: [] });
    setDlg(true);
  };
  const save = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      if (editing) await api.put(`/categories/${editing.id}`, form);
      else await api.post("/categories", form);
      toast.success(editing ? "Updated" : "Added");
      setDlg(false); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try { await api.delete(`/categories/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const KINDCOL = { income: "bg-green-50 text-green-700", expense: "bg-slate-100 text-slate-700", transfer: "bg-blue-50 text-blue-700" };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-500">{items.length} categories</div>
        {canEdit && <Button onClick={() => open()} className="rounded-md bg-red-600 hover:bg-red-700 text-white gap-2"><Plus className="h-4 w-4" /> Add category</Button>}
      </div>
      <div className="border border-slate-200 rounded-md bg-white overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>{["Name", "Kind", "Color", ""].map((h) => (
              <th key={h} className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/70">
                <td className="p-3 font-medium flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: c.color }} />
                  {c.name}
                  {c.is_system && <Lock className="h-3 w-3 text-slate-400" title="System" />}
                </td>
                <td className="p-3"><span className={`text-[10px] font-medium tracking-wider uppercase px-2 py-0.5 rounded ${KINDCOL[c.kind]}`}>{c.kind}</span></td>
                <td className="p-3 font-mono-fin text-slate-500 text-[11px]">{c.color}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  {canEdit && !c.is_system && (
                    <div className="inline-flex gap-1">
                      <button onClick={() => open(c)} className="p-1 text-slate-400 hover:text-slate-700"><Pencil className="h-3.5 w-3.5" /></button>
                      {canAdmin && <button onClick={() => remove(c.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit category" : "Add category"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" data-testid="cat-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kind</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Color</Label>
                <Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="mt-2 h-10 p-1" />
              </div>
            </div>
            <InvestmentTagsField
              value={form.investment_ids || []}
              onChange={(ids) => setForm({ ...form, investment_ids: ids })}
              testId="cat-inv-tags"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)} className="rounded-md">Cancel</Button>
            <Button onClick={save} className="rounded-md bg-red-600 hover:bg-red-700 text-white" data-testid="cat-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ----- Sub-categories ----- */
function SubCategoryTable({ canEdit, canAdmin }) {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [filter, setFilter] = useState("");
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", category_id: "", investment_ids: [] });

  const load = async () => {
    const [s, c] = await Promise.all([api.get("/subcategories"), api.get("/categories")]);
    setItems(s.data); setCats(c.data);
  };
  useEffect(() => { load(); }, []);

  const catMap = Object.fromEntries(cats.map((c) => [c.id, c]));

  const open = (e = null) => {
    setEditing(e);
    setForm(e ? { name: e.name, category_id: e.category_id, investment_ids: e.investment_ids || [] } : { name: "", category_id: filter || cats[0]?.id || "", investment_ids: [] });
    setDlg(true);
  };
  const save = async () => {
    if (!form.name || !form.category_id) return toast.error("Name and category required");
    try {
      if (editing) await api.put(`/subcategories/${editing.id}`, form);
      else await api.post("/subcategories", form);
      toast.success(editing ? "Updated" : "Added");
      setDlg(false); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this sub-category?")) return;
    try { await api.delete(`/subcategories/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const shown = filter ? items.filter((s) => s.category_id === filter) : items;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Label className="text-[12px]">Filter by category</Label>
          <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[220px] h-9 text-[12px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-sm text-slate-500 ml-2">{shown.length} shown</div>
        </div>
        {canEdit && <Button onClick={() => open()} className="rounded-md bg-red-600 hover:bg-red-700 text-white gap-2"><Plus className="h-4 w-4" /> Add sub-category</Button>}
      </div>
      <div className="border border-slate-200 rounded-md bg-white overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>{["Sub-category", "Under Category", ""].map((h) => (
              <th key={h} className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {shown.map((s) => {
              const c = catMap[s.category_id];
              return (
                <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50/70">
                  <td className="p-3 font-medium flex items-center gap-2">
                    {s.name}
                    {s.is_system && <Lock className="h-3 w-3 text-slate-400" />}
                  </td>
                  <td className="p-3 text-slate-600">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-sm" style={{ background: c?.color || "#64748B" }} />
                      {c?.name || "—"}
                    </span>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {canEdit && !s.is_system && (
                      <div className="inline-flex gap-1">
                        <button onClick={() => open(s)} className="p-1 text-slate-400 hover:text-slate-700"><Pencil className="h-3.5 w-3.5" /></button>
                        {canAdmin && <button onClick={() => remove(s.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit sub-category" : "Add sub-category"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Under category</Label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger className="mt-2"><SelectValue placeholder="Choose parent category" /></SelectTrigger>
                <SelectContent>
                  {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" data-testid="sub-name" /></div>
            <InvestmentTagsField
              value={form.investment_ids || []}
              onChange={(ids) => setForm({ ...form, investment_ids: ids })}
              testId="sub-inv-tags"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)} className="rounded-md">Cancel</Button>
            <Button onClick={save} className="rounded-md bg-red-600 hover:bg-red-700 text-white" data-testid="sub-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
